import { useQuery } from '@tanstack/react-query';

import useCompanyLegalEntity from '@/hooks/useCompanyLegalEntity';
import { getFiscalDataConfig } from '@/services/invoice';

export const NFSE_FISCAL_DATA_CONFIG_KEY = ['nfse-fiscal-data-config'];

/**
 * Estado da empresa para o botão de configuração de NFS-e: se já existem dados
 * fiscais cadastrados e se o usuário tem CNPJ.
 *
 * Quem responde se o vendedor tem CNPJ são os dados jurídicos, e não o
 * `companyCnpj` de `/financial/company/`.
 *
 * Aquele campo é apagado pelo webhook de conta do cakto-split quando chega
 * vazio, e o vendedor perdia o botão de configurar NFS-e mesmo tendo CNPJ. O
 * endpoint de dados jurídicos varre as fontes em cascata — a empresa do split,
 * o usuário do split e, por último, o próprio `companyCnpj` —, então ele nunca
 * enxerga menos do que o campo antigo enxergava.
 *
 * É também de onde o `initInvoiceForm` tira o CNPJ para abrir o wizard. Com
 * fontes diferentes, o botão liberava uma tela que devolvia "CNPJ do usuário
 * ainda não cadastrado", ou travava uma que abriria sem problema.
 *
 * O guard de impersonação mora dentro do `useCompanyLegalEntity`: a view tem
 * BlockImpersonateActions e a chamada só geraria um 403 previsível.
 */
export const useNFSeCompanyStatus = () => {
  const fiscalDataQuery = useQuery({
    queryKey: NFSE_FISCAL_DATA_CONFIG_KEY,
    queryFn: () => getFiscalDataConfig(),
    staleTime: 5 * 60 * 1000,
  });

  const { legalEntity, isLoadingLegalEntity, isErrorLegalEntity } = useCompanyLegalEntity();

  return {
    isConfiguredNFSe: Boolean(fiscalDataQuery.data),
    notaTesteStatus: fiscalDataQuery.data?.nota_teste_status ?? null,
    userHasCNPJ: Boolean(legalEntity?.cnpj),
    isLoading: fiscalDataQuery.isLoading || isLoadingLegalEntity,
    isError: fiscalDataQuery.isError || isErrorLegalEntity,
  };
};
